// Implements: F-MCK-015, F-UIX-007, F-UIX-015
/**
 * The tags of one mockup, each with its own named remove button when the person may tag.
 *
 * A list labelled "Etiquetas" so a screen reader announces how many there are; each remove button
 * carries the tag in its name ("Quitar la etiqueta ventas"), because an "×" alone does not say
 * which one it removes. No tag at all is said in words, never shown as an empty box.
 *
 * The buttons are never disabled while a write is in flight: disabling the focused button would
 * drop the keyboard focus to the page (WCAG 2.4.3). The container ignores a press meanwhile.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import styles from "./MockupTagList.module.css";

export interface MockupTagListProps {
  readonly tags: readonly string[];
  /** `undefined` when the person may not tag: no button is offered. */
  readonly onRemove?: ((tag: string) => void) | undefined;
}

export function MockupTagList({ tags, onRemove }: MockupTagListProps): ReactElement {
  const { t } = useTranslation();
  if (tags.length === 0) return <p className={styles.none}>{t("mockup.detail.noTags")}</p>;
  return (
    <ul className={styles.tags} aria-label={t("mockup.detail.tags")}>
      {tags.map((tag) => (
        <li key={tag} className={styles.tag}>
          <span className={styles.text}>{tag}</span>
          {onRemove === undefined ? null : (
            <Button
              variant="ghost"
              size="sm"
              label={t("mockup.detail.removeTag", { tag })}
              onClick={() => {
                onRemove(tag);
              }}
            >
              <Icon name="close" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
